import Lottie from "lottie-react";
import { useState, useEffect } from "react";

import { cn } from '@/lib/utils';
import ardealyAnimation from "../../../assets/ardealy-animation.json";

export interface LoadingOverlayProps {
  open: boolean;
  message?: string;
  transparent?: boolean;
}

/**
 * The full-screen wait, with the Ardealy mark animating.
 *
 * It renders past `open === false` for the length of its exit animation —
 * without that the overlay vanished the instant a request resolved, which read
 * as a flicker rather than as a finish.
 */
export function LoadingOverlay({ open, message = "Loading...", transparent = false }: LoadingOverlayProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      // Start closing animation
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 400); // Match fadeOut animation duration
      return () => clearTimeout(timer);
    }
  }, [open, shouldRender]);

  if (!shouldRender) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[10000] flex items-center justify-center',
        transparent ? 'bg-muted/90 backdrop-blur-md' : 'bg-muted',
        isClosing ? 'animate-out fade-out duration-400' : 'animate-in fade-in duration-500',
      )}
    >
      <div className="flex flex-col items-center justify-center">
        <div
          className={cn(
            'mb-5 size-20',
            isClosing ? 'animate-out fade-out duration-400' : 'animate-in zoom-in-95 duration-400',
          )}
        >
          <Lottie animationData={ardealyAnimation} loop autoplay />
        </div>

        {/* Keyed on the message so a new phase re-plays the entrance. */}
        <p
          key={message}
          className={cn(
            'text-base tracking-wide',
            isClosing
              ? 'animate-out fade-out duration-300'
              : 'animate-in fade-in slide-in-from-bottom-2 duration-400',
          )}
        >
          {message}
        </p>
      </div>
    </div>
  );
}
